import { useEffect, useState } from 'react';

import type { MyAddress } from '../api/types.ts';
import { errorText, useStrings } from '../i18n/index.ts';
import {
  addressDetailsLine,
  GUEST_ADDRESS_ID,
  removeAddress,
  requestCoverage,
  selectAndRefresh,
  selectedAddress,
  setGuest,
  useAddressBook,
} from '../state/address-book.ts';
import { popRoute, pushRoute } from '../state/router.ts';
import { Icon } from '../ui/icons.tsx';
import { cx, ErrorBanner, ScreenTitle, Spinner } from '../ui/kit.tsx';
import { Screen } from '../ui/Screen.tsx';
import { NewAddressScreen } from './NewAddressScreen.tsx';
import '../styles/account.css';

/**
 * Выбор адреса доставки. Адрес определяет склад, а с ним витрину и тариф: смена адреса
 * перечитывает и то и другое.
 */
export function AddressesScreen() {
  const strings = useStrings();
  const book = useAddressBook();

  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  // Просьбы «сообщите, когда начнёте» этого открытия экрана: отправляется и принята.
  const [coverage, setCoverage] = useState<Record<string, 'sending' | 'sent'>>({});

  // Режим экрана фиксируется при открытии: после сохранения первого адреса список уже не
  // пуст, но экран не должен смениться под пальцем — он закрывается.
  const empty = book.addresses.length === 0 && book.guest === null && !book.isLoading;
  const [opensNewAddress, setOpensNewAddress] = useState<boolean | null>(book.isLoading ? null : empty);
  useEffect(() => {
    if (opensNewAddress === null && !book.isLoading) setOpensNewAddress(empty);
  }, [opensNewAddress, book.isLoading, empty]);

  // Выбирать не из чего: экран сразу становится экраном нового адреса.
  if (opensNewAddress ?? empty) return <NewAddressScreen />;

  // Сохранённые адреса или точка гостя, пока аккаунта нет.
  const selected = selectedAddress(book);
  const rows: MyAddress[] = book.addresses.length > 0 ? book.addresses : selected?.id === GUEST_ADDRESS_ID ? [selected] : [];

  const select = async (address: MyAddress) => {
    try {
      if (address.id !== GUEST_ADDRESS_ID) await selectAndRefresh(address.id);
      popRoute();
    } catch (failure) {
      setError(errorText(strings, failure));
    }
  };

  const remove = async (address: MyAddress) => {
    setRemoving(address.id);
    try {
      if (address.id === GUEST_ADDRESS_ID) setGuest(null);
      else await removeAddress(address.id);
    } catch (failure) {
      setError(errorText(strings, failure));
    } finally {
      setRemoving(null);
    }
  };

  const askCoverage = async (address: MyAddress) => {
    setCoverage((state) => ({ ...state, [address.id]: 'sending' }));
    try {
      await requestCoverage(address.id);
      setCoverage((state) => ({ ...state, [address.id]: 'sent' }));
    } catch (failure) {
      setCoverage((state) => {
        const { [address.id]: _failed, ...rest } = state;
        return rest;
      });
      setError(errorText(strings, failure));
    }
  };

  return (
    <Screen>
      <div className="readable stack addresses">
        <ScreenTitle text={strings.myAddresses} />

        <div className="gutter stack addresses-body">
          {rows.length > 0 && (
            <div className="card flush">
              {rows.map((address) => {
                const isSelected = selected?.id === address.id;
                const details = addressDetailsLine(address, strings);
                return (
                  <div key={address.id} className="address-row">
                    <div className="grow stack">
                      <button type="button" className="row address-pick" disabled={!address.deliverable} onClick={() => void select(address)}>
                        <Icon
                          name={isSelected ? 'check' : address.deliverable ? 'pin' : 'pinOff'}
                          size={22}
                          weight={isSelected ? 2.8 : 2}
                          className={cx('address-mark', isSelected && 'is-selected')}
                        />
                        <span className="grow stack address-text">
                          <span className={cx('address-title', !address.deliverable && 't-muted')}>{address.text}</span>
                          {details && <span className="t-footnote t-muted">{details}</span>}
                          {address.comment && <span className="t-footnote t-muted">{address.comment}</span>}
                          {!address.deliverable && <span className="t-caption address-off">{strings.notDeliverable}</span>}
                        </span>
                      </button>
                      {!address.deliverable &&
                        (coverage[address.id] === 'sent' ? (
                          <span className="row t-footnote address-coverage is-sent">
                            <Icon name="check" size={14} weight={2.8} />
                            {strings.coverageRequested}
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="text-button t-footnote address-coverage"
                            disabled={coverage[address.id] === 'sending'}
                            onClick={() => void askCoverage(address)}
                          >
                            {strings.coverageRequest}
                          </button>
                        ))}
                    </div>

                    {/* Удаление кнопкой в строке: у карточек нет свайпов. */}
                    <button type="button" className="address-remove" aria-label={strings.delete} disabled={removing !== null} onClick={() => void remove(address)}>
                      {removing === address.id ? <Spinner size={16} /> : <Icon name="trash" size={15} weight={2.2} />}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <button type="button" className="card row address-add" onClick={() => pushRoute({ name: 'newAddress' })}>
            <Icon name="plus" size={20} weight={2.6} />
            {strings.newAddress}
          </button>

          {error && <ErrorBanner text={error} />}
        </div>
      </div>
    </Screen>
  );
}
